import {
  Box,
  Heading,
  HStack,
  Image,
  Text,
  VStack
} from "@chakra-ui/react";

import { siteFeaturedGame } from "@/data/games";
import { nvpTokens } from "@/design-system/tokens";
import { useI18n } from "@/i18n";
import { GlowButton } from "@/shared/ui/GlowButton";
import { AppleStoreIcon, GooglePlayIcon } from "@/shared/ui/StoreIcons";

export function FeaturedGame() {
  const { copy, localize } = useI18n();
  const game = siteFeaturedGame;
  const backgroundImage = game.featuredBackground ?? game.heroImage;
  const logoImage = game.featuredLogo ?? game.logo;

  return (
    <Box
      id="featured"
      as="section"
      position="relative"
      overflow="hidden"
      minH={nvpTokens.landing.featured.minHeight}
      border="4px solid transparent"
      borderRadius={nvpTokens.radius.card}
      bg={`linear-gradient(${nvpTokens.color.surface.base}, ${nvpTokens.color.surface.base}) padding-box, linear-gradient(103deg, ${nvpTokens.color.primary.electricBlue} 0%, ${nvpTokens.color.primary.cosmicViolet} 28%, ${nvpTokens.color.primary.neonPink} 68%, ${nvpTokens.color.primary.brightYellow} 100%) border-box`}
      boxShadow="0 34px 86px rgba(0, 0, 0, 0.44)"
    >
      <Image
        src={backgroundImage}
        alt={localize(game.title)}
        position="absolute"
        inset={0}
        h="100%"
        w="100%"
        objectFit="cover"
        objectPosition={{ base: "54% center", md: "center" }}
        filter="saturate(1.12) contrast(1.08)"
      />
      <Box
        aria-hidden="true"
        position="absolute"
        inset={0}
        bg="linear-gradient(90deg, rgba(2, 3, 13, 0.94) 0%, rgba(5, 7, 25, 0.84) 38%, rgba(5, 7, 25, 0.42) 62%, rgba(2, 3, 13, 0.1) 100%), linear-gradient(180deg, rgba(255, 79, 216, 0.14) 0%, rgba(0, 2, 8, 0) 34%, rgba(0, 2, 8, 0.24) 100%)"
      />
      <Image
        src={logoImage}
        alt=""
        aria-hidden="true"
        position="absolute"
        display={{ base: "none", md: "block" }}
        right={nvpTokens.landing.featured.contentPadding}
        top={{ base: "48%", md: "50%" }}
        transform="translateY(-50%)"
        w={nvpTokens.landing.featured.logoWidth}
        maxW="680px"
        opacity={{ base: 0.48, md: 0.98 }}
        filter="drop-shadow(0 26px 34px rgba(0, 0, 0, 0.56)) drop-shadow(0 0 32px rgba(255, 79, 216, 0.24))"
        pointerEvents="none"
        userSelect="none"
        loading="lazy"
        decoding="async"
      />
      <VStack
        position="relative"
        zIndex={1}
        align="start"
        justify="center"
        spacing={5}
        minH={nvpTokens.landing.featured.minHeight}
        maxW={nvpTokens.landing.featured.contentMaxWidth}
        p={nvpTokens.landing.featured.contentPadding}
      >
        <Box
          as="span"
          display="inline-flex"
          border="2px solid transparent"
          borderRadius={nvpTokens.radius.pill}
          bg={`linear-gradient(rgba(9, 11, 32, 0.74), rgba(9, 11, 32, 0.74)) padding-box, linear-gradient(92deg, ${nvpTokens.color.primary.electricBlue}, ${nvpTokens.color.primary.cosmicViolet}, ${nvpTokens.color.primary.neonPink}) border-box`}
          boxShadow="0 0 22px rgba(55, 213, 255, 0.24), inset 0 0 18px rgba(255, 255, 255, 0.08)"
          px={4}
          py={2}
        >
          <Text
            color={nvpTokens.color.primary.electricBlue}
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight={900}
            lineHeight={1}
            textTransform="uppercase"
            textShadow="0 0 16px rgba(55, 213, 255, 0.82)"
          >
            {copy.featured.eyebrow}
          </Text>
        </Box>
        <VStack align="start" spacing={4}>
          <Heading
            as="h2"
            color="white"
            fontSize={nvpTokens.landing.featured.titleFontSize}
            lineHeight={nvpTokens.landing.featured.titleLineHeight}
            fontWeight={900}
            textTransform="uppercase"
            textShadow="0 2px 24px rgba(0, 0, 0, 0.72)"
          >
            {copy.featured.title}
          </Heading>
          <Text
            maxW="460px"
            color="whiteAlpha.880"
            fontSize={{ base: "md", md: "lg" }}
            lineHeight={1.5}
            textShadow="0 2px 24px rgba(0, 0, 0, 0.72)"
          >
            {localize(game.tagline)}
          </Text>
          <HStack flexWrap="wrap" gap={3} pt={2}>
            <GlowButton
              as="a"
              href={game.links.appStore}
              target="_blank"
              rel="noreferrer"
              leftIcon={<AppleStoreIcon size={18} />}
              glow="pink"
              variantKind="secondary"
            >
              {copy.featured.appStore}
            </GlowButton>
            <GlowButton
              as="a"
              href={game.links.googlePlay}
              target="_blank"
              rel="noreferrer"
              leftIcon={<GooglePlayIcon size={18} />}
              glow="blue"
              variantKind="secondary"
            >
              {copy.featured.googlePlay}
            </GlowButton>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
}
